const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          const words = valueToSearch.split(' ').filter(word => word.length > 0);

          const fetchPromises = words.map(word => {
            let ajaxUrl;
            if (theme.Shopify.features.predictiveSearch) {
              ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
              ajaxUrl.searchParams.set('q', word);
              ajaxUrl.searchParams.set('section_id', 'predictive-search');
              ajaxUrl.searchParams.set('resources[limit]', resultLimit);
              ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
            } else {
              ajaxUrl = new URL(linkURL);
              ajaxUrl.searchParams.set('q', word);
              ajaxUrl.searchParams.set('section_id', 'main-search');
            }

            return fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
              .then(response => {
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                return response.text();
              })
              .then(text => ({ word, text }))
              .catch(err => {
                if (err.name === 'AbortError') return null;
                return null;
              });
          });

          Promise.all(fetchPromises).then((responses) => {
            if (searchAbortController === null) return; // Batch was already aborted

            const resultsByWord = responses.filter(r => r !== null);
            const allProductsByWord = [];
            const allPagesByWord = [];

            resultsByWord.forEach(({ word, text }) => {
              const template = document.createElement('template');
              template.innerHTML = text;
              let fetchedList = null;
              if (theme.Shopify.features.predictiveSearch) {
                fetchedList = template.content.querySelector('.product-grid');
              } else {
                fetchedList = template.content.querySelector('.section-search-template .product-grid');
              }

              if (fetchedList) {
                const products = Array.from(fetchedList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)'));
                const pages = Array.from(fetchedList.querySelectorAll('.product-block.page-block'));
                allProductsByWord.push({ word, items: products });
                allPagesByWord.push({ word, items: pages });
              }
            });

            // Interleave logic
            const interleavedProducts = [];
            const interleavedPages = [];
            const seenUrls = new Set();
            const maxRounds = Math.max(...allProductsByWord.map(w => w.items.length), ...allPagesByWord.map(w => w.items.length));

            for (let i = 0; i < maxRounds; i++) {
              allProductsByWord.forEach(wordGroup => {
                const item = wordGroup.items[i];
                if (item) {
                  const url = item.querySelector('a')?.href;
                  if (url && !seenUrls.has(url)) {
                    seenUrls.add(url);
                    interleavedProducts.push(item);
                  }
                }
              });
              allPagesByWord.forEach(wordGroup => {
                const item = wordGroup.items[i];
                if (item) {
                  const url = item.querySelector('a')?.href;
                  if (url && !seenUrls.has(url)) {
                    seenUrls.add(url);
                    interleavedPages.push(item);
                  }
                }
              });
            }

            // reset previous contents
            searchAbortController = null;
            this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
            resultsBox.innerHTML = '';

            const resultsProducts = document.createElement('div');
            resultsProducts.className = 'main-search__results__products collection-listing';
            resultsProducts.innerHTML = '<div></div>';
            const resultsPages = document.createElement('div');
            resultsPages.className = 'main-search__results__pages';

            if (interleavedProducts.length > 0 || interleavedPages.length > 0) {
              // Get original grid class from the first available list or just use default
              resultsProducts.firstElementChild.className = 'product-grid product-grid--per-row-4 product-grid--per-row-mob-2';

              // Convert standard blocks into quick search result format
              interleavedProducts.slice(0, resultLimit + 1).forEach((block) => {
                block.classList.add('main-search-result');
                block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                resultsProducts.firstElementChild.appendChild(block);
              });

              interleavedPages.forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');

              if (areProducts || arePages) {
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', interleavedProducts.length > 4);
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
              } else {
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
              }
            } else {
              this.classList.remove('main-search--loading');
              this.classList.add('main-search--no-results');
              const emptyMessage = document.createElement('div');
              emptyMessage.className = 'main-search__empty-message';
              emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
              resultsBox.appendChild(emptyMessage);
            }
          });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
